import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { episodeTripWindow, tripsForEpisode } from "@/lib/alertTrips";
import Layout from "@/components/Layout";
import api from "@/lib/api";
import HistoryMap, { tripColor, buildColorMap } from "@/components/HistoryMap";
import ComparePanel from "@/components/ComparePanel";
import { GitCompare } from "lucide-react";
import BrandLoader from "@/components/BrandLoader";
import { Calendar } from "@/components/ui/calendar";
import { useAuth } from "@/context/AuthContext";
import { clampFrom, limitDays, limitText } from "@/lib/reportLimit";
import { getVehicleIcon } from "@/lib/fleet";
import { fr } from "date-fns/locale";
import { History, Route, Flag, Clock, Gauge, ChevronDown, Check, Car, CalendarRange, Loader2, MapPin, FileText, FileSpreadsheet, Download, User, LineChart, Cpu, Fuel } from "lucide-react";
import { exportTripsCSV, exportTripsPDF, exportTripsGPX, exportDriverReportPDF } from "@/lib/historyExport";
import TripChartsModal from "@/components/TripChartsModal";
import { fmtTime as fmtTimeS, distStr, getUserSettings } from "@/lib/userSettings";
import { fmtXPF, fuelReason, fuelKey, fuelCostHint } from "@/lib/fuelCost";
import { useAgent } from "@/context/AgentContext";

const ymd = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const fmtDur = (m) => (m >= 60 ? `${Math.floor(m / 60)}h${String(m % 60).padStart(2, "0")}` : `${m} min`);
const fmtHM = (iso) => fmtTimeS(iso);
const fmtDay = (iso) => new Date(iso).toLocaleDateString(getUserSettings().language === "en" ? "en-US" : "fr-FR", { timeZone: getUserSettings().timezone || "Pacific/Noumea", weekday: "short", day: "2-digit", month: "2-digit" });

// Jour calendaire "aujourd'hui" dans le FUSEAU DU COMPTE (jamais le fuseau du navigateur).
// Corrige la sélection « Aujourd'hui/Hier… » quand le navigateur n'est pas réglé sur l'heure de Nouméa :
// les raccourcis sont envoyés au backend en date-seule, interprétée dans le fuseau de l'entreprise.
const accountToday = () => {
  const tz = getUserSettings().timezone || "Pacific/Noumea";
  const p = new Intl.DateTimeFormat("en-CA", { timeZone: tz, year: "numeric", month: "2-digit", day: "2-digit" }).formatToParts(new Date());
  const get = (t) => Number(p.find((x) => x.type === t).value);
  return new Date(get("year"), get("month") - 1, get("day"));  // minuit local portant la date calendaire du compte
};

const SHORTCUTS = [
  { id: "today", label: "Aujourd'hui" },
  { id: "yesterday", label: "Hier" },
  { id: "7d", label: "7 derniers jours", days: 7 },
  { id: "30d", label: "30 derniers jours", days: 30 },
];

function rangeFor(id) {
  const t = accountToday();
  if (id === "today") return { from: t, to: t };
  if (id === "yesterday") { const y = new Date(t); y.setDate(y.getDate() - 1); return { from: y, to: y }; }
  if (id === "7d") { const f = new Date(t); f.setDate(f.getDate() - 6); return { from: f, to: t }; }
  if (id === "30d") { const f = new Date(t); f.setDate(f.getDate() - 29); return { from: f, to: t }; }
  return { from: t, to: t };
}

export default function GlobalHistoryPage() {
  const location = useLocation();
  const episodeId = new URLSearchParams(location.search).get("episode_id");
  const [alertContext, setAlertContext] = useState(null);
  const [alertError, setAlertError] = useState("");
  const [vehicles, setVehicles] = useState([]);
  const [selVeh, setSelVeh] = useState([]); // [] = toute la flotte
  const [vehOpen, setVehOpen] = useState(false);
  const { user } = useAuth();
  const agent = useAgent();
  const lim = limitDays(user);
  const [shortcut, setShortcut] = useState("today");
  const [range, setRange] = useState(rangeFor("today"));

  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [selectedIdx, setSelectedIdx] = useState(null);
  const [hoveredIdx, setHoveredIdx] = useState(null);
  const [addr, setAddr] = useState({});
  const [drivers, setDrivers] = useState([]);
  const [selDriver, setSelDriver] = useState(""); // "" = tous les conducteurs
  const [chartTrip, setChartTrip] = useState(null);
  const [fuelMap, setFuelMap] = useState(null); // {key -> cell} ; coût carburant par trajet (lecture seule)
  const [fuelPriceBasis, setFuelPriceBasis] = useState(null);
  const appliedRange = useRef(null);
  const [mobileView, setMobileView] = useState("list"); // 'list' | 'map' (mobile uniquement)
  const [calOpen, setCalOpen] = useState(false);

  // A10 — Deep-link « hors horaires » : ?episode=<id>&focus=trigger|trip
  const [focusPoint, setFocusPoint] = useState(null);        // mesure GPS historique retenue (jamais position actuelle)
  const [deepLink, setDeepLink] = useState(null);            // {focus, status, ctx}
  const deepLinkDone = useRef(false);

  // Mode COMPARAISON (V1, frontend uniquement, min 2 / max 4 trajets déjà chargés)
  const [compareMode, setCompareMode] = useState(false);
  const [compareIdxs, setCompareIdxs] = useState([]);
  const [showComparePanel, setShowComparePanel] = useState(false);
  const [hoverCompareIdx, setHoverCompareIdx] = useState(null);
  const [recenterIdx, setRecenterIdx] = useState(null);
  // Préservation explicite de la position de défilement de la liste des trajets (retour depuis le détail).
  const tripListRef = useRef(null);
  const listScrollRef = useRef(0);
  const backToList = () => {
    setMobileView("list");
    requestAnimationFrame(() => { if (tripListRef.current) tripListRef.current.scrollTop = listScrollRef.current; });
  };
  const MAX_COMPARE = (typeof window !== "undefined" && window.innerWidth < 1024) ? 3 : 4;

  const toggleCompareMode = () => {
    setCompareMode((on) => {
      const next = !on;
      if (!next) { setCompareIdxs([]); setShowComparePanel(false); setHoverCompareIdx(null); }
      else { setSelectedIdx(null); }
      return next;
    });
  };
  const toggleCompareTrip = (i) => {
    setCompareIdxs((arr) => {
      if (arr.includes(i)) return arr.filter((x) => x !== i);
      if (arr.length >= MAX_COMPARE) return arr;   // plafond lisibilité
      return [...arr, i];
    });
  };
  const removeCompare = (i) => setCompareIdxs((arr) => {
    const next = arr.filter((x) => x !== i);
    if (next.length < 2) setShowComparePanel(false);
    return next;
  });
  const recenterCompare = (i) => { setRecenterIdx(null); requestAnimationFrame(() => setRecenterIdx(i)); if (typeof window !== "undefined" && window.innerWidth < 1024) setMobileView("map"); };

  const selectTrip = (i) => {
    if (tripListRef.current) listScrollRef.current = tripListRef.current.scrollTop;  // mémorise le défilement avant le détail
    setSelectedIdx((s) => (s === i ? null : i));
    if (typeof window !== "undefined" && window.innerWidth < 1024) setMobileView("map");
  };

  useEffect(() => {
    api.get("/vehicles").then((r) => setVehicles(r.data || [])).catch(() => setVehicles([]));
    api.get("/drivers").then((r) => setDrivers(r.data || [])).catch(() => setDrivers([]));
  }, []);

  // A10 — Deep-link épisode « hors horaires ». Re-fetch PROTÉGÉ (RBAC/tenant backend) du contexte de
  // déclenchement, pré-sélection véhicule + jour (Pacific/Noumea), chargement du trajet, puis focus
  // (position mesurée réelle OU trajet couvrant le déclenchement). Jamais de position actuelle en repli.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const eid = params.get("episode");
    const focus = params.get("focus");
    if (!eid || deepLinkDone.current) return;
    deepLinkDone.current = true;
    (async () => {
      setDeepLink({ focus, status: "loading" });
      try {
        const ctx = (await api.get(`/alert-episodes/${encodeURIComponent(eid)}/trigger-context`)).data;
        if (!ctx.vehicle_id || !ctx.trigger_day) { setDeepLink({ focus, status: "error" }); return; }
        setSelVeh([ctx.vehicle_id]);
        const [y, m, d] = ctx.trigger_day.split("-").map(Number);
        const dayDate = new Date(y, m - 1, d);
        setRange({ from: dayDate, to: dayDate });
        setShortcut("custom");
        appliedRange.current = { from: ctx.trigger_day, to: ctx.trigger_day };
        setLoading(true); setData(null); setSelectedIdx(null); setAddr({}); setFocusPoint(null);
        const qp = new URLSearchParams({ date_from: ctx.trigger_day, date_to: ctx.trigger_day, vehicle_ids: ctx.vehicle_id });
        const r = await api.get(`/fleet-trips?${qp.toString()}`);
        setData(r.data);
        setLoading(false);
        const tripsLoaded = r.data?.trips || [];
        if (focus === "trip") {
          const trigMs = new Date(ctx.triggered_at).getTime();
          let idx = tripsLoaded.findIndex((t) => new Date(t.start).getTime() <= trigMs && trigMs <= new Date(t.end).getTime());
          if (idx < 0) {
            let best = null;
            tripsLoaded.forEach((t, i) => {
              const s = new Date(t.start).getTime(), e = new Date(t.end).getTime();
              const gap = trigMs < s ? s - trigMs : (trigMs > e ? trigMs - e : 0);
              if (gap <= 1800000 && (best == null || gap < best.gap)) best = { i, gap };
            });
            if (best) idx = best.i;
          }
          if (idx >= 0) { setSelectedIdx(idx); setDeepLink({ focus, status: "ready", ctx }); }
          else setDeepLink({ focus, status: "trip_unavailable", ctx });
        } else {
          if (ctx.trigger_point) { setFocusPoint(ctx.trigger_point); setDeepLink({ focus, status: "ready", ctx }); }
          else setDeepLink({ focus, status: "trigger_unavailable", ctx });
        }
      } catch {
        setDeepLink({ focus, status: "error" });
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // --- Mode AGENT : signale le montage puis exécute les commandes (sélection/période/chargement) ---
  useEffect(() => { agent?.ready("historique"); /* eslint-disable-next-line */ }, []);
  useEffect(() => {
    const cmd = agent?.histCmd;
    if (!cmd) return;
    (async () => {
      if (cmd.type === "select") {
        setSelVeh([cmd.vehicle_id]); setVehOpen(false);
        agent.ack(cmd.nonce, true);
      } else if (cmd.type === "period") {
        const [fy, fm, fd] = cmd.date_from.split("-").map(Number);
        const [ty, tm, td] = cmd.date_to.split("-").map(Number);
        setRange({ from: new Date(fy, fm - 1, fd), to: new Date(ty, tm - 1, td) });
        setShortcut("custom");
        agent.ack(cmd.nonce, true);
      } else if (cmd.type === "load") {
        setLoading(true); setData(null); setSelectedIdx(null); setAddr({}); setCalOpen(false);
        setFuelMap(null); setFuelPriceBasis(null);
        appliedRange.current = { from: cmd.date_from, to: cmd.date_to };
        try {
          const d = await agent.runHistoryLoad({ vehicle_id: cmd.vehicle_id, date_from: cmd.date_from, date_to: cmd.date_to });
          setData(d); agent.ack(cmd.nonce, { count: d.count });
        } catch {
          setData({ count: 0, trips: [], total_km: 0 }); agent.ack(cmd.nonce, { count: 0 });
        } finally { setLoading(false); }
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agent?.histCmd]);

  const pickShortcut = (id) => {
    setShortcut(id);
    setRange(rangeFor(id));
  };

  const toggleVeh = (id) => setSelVeh((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

  const vehLabel = selVeh.length === 0 ? "Toute la flotte" : selVeh.length === 1 ? (vehicles.find((v) => v.id === selVeh[0])?.name || "1 véhicule") : `${selVeh.length} véhicules`;

  const validate = async () => {
    setAlertContext(null); setAlertError("");
    const from = range?.from || new Date();
    const to = range?.to || range?.from || from;
    setLoading(true); setData(null); setSelectedIdx(null); setAddr({}); setCalOpen(false);
    setFuelMap(null); setFuelPriceBasis(null);
    setFocusPoint(null); setDeepLink(null);
    appliedRange.current = { from: ymd(from), to: ymd(to) };
    try {
      const params = new URLSearchParams({ date_from: ymd(from), date_to: ymd(to) });
      if (selVeh.length) params.set("vehicle_ids", selVeh.join(","));
      if (selDriver) params.set("driver_id", selDriver);
      const r = await api.get(`/fleet-trips?${params.toString()}`);
      setData(r.data);
    } catch { setData({ count: 0, trips: [], total_km: 0 }); }
    setLoading(false);
  };

  // Resolve the authorized episode before querying any vehicle history.
  // Backend access checks still apply to both requests; no public sharing token.
  useEffect(() => {
    if (!episodeId || !user) return;
    let cancelled = false;
    setLoading(true); setData(null); setAlertError(""); setAlertContext(null);
    setSelectedIdx(null); setAddr({}); setSelDriver("");
    (async () => {
      try {
        const { data: ep } = await api.get(`/alert-episodes/${encodeURIComponent(episodeId)}`);
        if (cancelled) return;
        const window = episodeTripWindow(ep);
        const params = new URLSearchParams({ date_from: window.date_from, date_to: window.date_to,
          vehicle_ids: window.vehicle_id });
        const { data: result } = await api.get(`/fleet-trips?${params}`);
        if (cancelled) return;
        const relevant = tripsForEpisode(result.trips || [], window);
        setSelVeh([window.vehicle_id]); setShortcut("");
        const calendarDate = (s) => { const [y, m, d] = s.split("-").map(Number); return new Date(y, m - 1, d); };
        setRange({ from: calendarDate(window.date_from), to: calendarDate(window.date_to) });
        setAlertContext(ep);
        setData({ ...result, trips: relevant, count: relevant.length,
          total_km: relevant.reduce((sum, t) => sum + (t.distance_km || 0), 0) });
        if (relevant.length === 1) setSelectedIdx(0);
      } catch (error) {
        if (!cancelled) { setData(null); setAlertError(error.response?.status === 404
          ? "Cette alerte est introuvable ou inaccessible avec votre compte."
          : "Impossible de charger les trajets de cette alerte. Réessayez depuis le lien reçu."); }
      } finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [episodeId, user]);

  // Reverse geocode progressif — limité aux 25 premiers trajets (évite de marteler Nominatim)
  useEffect(() => {
    if (!data?.trips?.length) return;
    let cancel = false;
    (async () => {
      const cap = Math.min(data.trips.length, 25);
      for (let i = 0; i < cap; i++) {
        const t = data.trips[i];
        if (!t.points?.length || addr[i]) continue;
        const s = t.points[0], e = t.points[t.points.length - 1];
        try {
          const rs = await api.get(`/geocode/reverse?lat=${s.lat}&lon=${s.lon}`);
          const re = await api.get(`/geocode/reverse?lat=${e.lat}&lon=${e.lon}`);
          if (cancel) return;
          setAddr((a) => ({ ...a, [i]: { start: rs.data.address, end: re.data.address } }));
        } catch { /* ignore */ }
      }
    })();
    return () => { cancel = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  // Géocode à la demande le trajet sélectionné (si au-delà des 25 premiers)
  useEffect(() => {
    if (selectedIdx == null) return;
    const t = trips[selectedIdx];
    if (!t?.points?.length || addr[selectedIdx]) return;
    let cancel = false;
    (async () => {
      const s = t.points[0], e = t.points[t.points.length - 1];
      try {
        const rs = await api.get(`/geocode/reverse?lat=${s.lat}&lon=${s.lon}`);
        const re = await api.get(`/geocode/reverse?lat=${e.lat}&lon=${e.lon}`);
        if (!cancel) setAddr((a) => ({ ...a, [selectedIdx]: { start: rs.data.address, end: re.data.address } }));
      } catch { /* ignore */ }
    })();
    return () => { cancel = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedIdx]);

  const trips = data?.trips || [];
  // Coût carburant par trajet : UN appel PAR VÉHICULE (pas par trajet) pour la perf, concurrence bornée.
  // Jointure par (vehicle_id, start) — segmentation identique entre /fleet-trips et /trip-fuel-cost.
  useEffect(() => {
    if (!data?.trips?.length || !appliedRange.current) { return; }
    const r = appliedRange.current;
    const vids = [...new Set(data.trips.map((t) => t.vehicle_id).filter(Boolean))];
    let cancel = false;
    (async () => {
      const map = {};
      let basis = null;
      const pool = 4;
      for (let k = 0; k < vids.length; k += pool) {
        if (cancel) return;
        const batch = vids.slice(k, k + pool);
        const res = await Promise.all(batch.map((vid) =>
          api.get(`/vehicles/${vid}/trip-fuel-cost?date_from=${r.from}&date_to=${r.to}`)
            .then((rr) => rr.data).catch(() => null)));
        res.forEach((rd) => {
          if (!rd) return;
          if (rd.price_basis) basis = rd.price_basis;
          (rd.trips || []).forEach((t) => { map[fuelKey(rd.vehicle_id, t.start)] = t; });
        });
        if (!cancel) { setFuelMap({ ...map }); if (basis) setFuelPriceBasis(basis); }
      }
    })();
    return () => { cancel = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  const colorMap = useMemo(() => buildColorMap(data?.trips || []), [data]);
  const listColor = (i) => (colorMap ? colorMap[(trips[i] || {}).vehicle_id] : tripColor(i));
  const summary = useMemo(() => {
    const tp = data?.trips || [];
    if (!tp.length) return null;
    const totalKm = tp.reduce((s, t) => s + (t.distance_km || 0), 0);
    const totalMin = tp.reduce((s, t) => s + (t.duration_min || 0), 0);
    const byVeh = {}, byDrv = {};
    tp.forEach((t) => {
      byVeh[t.vehicle_name] = (byVeh[t.vehicle_name] || 0) + (t.distance_km || 0);
      if (t.driver_name) byDrv[t.driver_name] = (byDrv[t.driver_name] || 0) + (t.distance_km || 0);
    });
    const topVeh = Object.entries(byVeh).sort((a, b) => b[1] - a[1])[0];
    const topDrv = Object.entries(byDrv).sort((a, b) => b[1] - a[1])[0];
    return { totalKm: Math.round(totalKm * 10) / 10, totalMin: Math.round(totalMin), topVeh, topDrv };
  }, [data]);
  const rangeText = useMemo(() => {
    const from = range?.from, to = range?.to || range?.from;
    if (!from) return "";
    if (ymd(from) === ymd(to)) return from.toLocaleDateString("fr-FR", { weekday: "long", day: "2-digit", month: "long" });
    return `Du ${from.toLocaleDateString("fr-FR", { day: "2-digit", month: "short" })} au ${to.toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "numeric" })}`;
  }, [range]);

  return (
    <Layout fullBleed>
      <div className="relative flex h-[calc(100vh-8rem)] lg:h-[calc(100vh-4rem)] w-full overflow-hidden bg-background" data-testid="history-workspace">
        {/* Drawer trajets */}
        <div className={`${mobileView === "list" ? "flex" : "hidden"} lg:flex w-full lg:max-w-[380px] shrink-0 flex-col border-r border-border bg-card/60 overflow-hidden`} data-testid="history-panel">
          {/* Filtres */}
          <div className="p-3 border-b border-border space-y-2.5">
            <p className="label-mono flex items-center gap-1.5"><History className="w-4 h-4 brand-teal" /> Historique de flotte</p>
            {alertError && <p role="alert" className="text-sm text-red-500">{alertError}</p>}
            {alertContext && <div className="rounded-md border border-border p-2 text-sm" data-testid="history-alert-context">
              <p className="font-semibold">{alertContext.rule_name || "Trajets concernés par l'alerte"}</p>
              <p>{alertContext.vehicle} · {alertContext.plate}</p>
              <p>Du {fmtDay(alertContext.started_at)} à {fmtHM(alertContext.started_at)}
                {alertContext.ended_at ? ` au ${fmtDay(alertContext.ended_at)} à ${fmtHM(alertContext.ended_at)}` : " · Alerte en cours"}</p>
              {!data?.trips?.length && <p>Aucun trajet reçu sur cette période. Rechargez ce lien si le déplacement vient de commencer.</p>}
            </div>}

            {/* Sélection véhicules */}
            <div className="relative">
              <button data-testid="history-vehicle-select" onClick={() => setVehOpen((o) => !o)}
                className={`w-full flex items-center justify-between gap-2 rounded-md border bg-secondary/40 px-3 py-2 text-sm hover:border-[#17A2B8] transition-colors ${agent?.highlight === "vehicle" ? "border-[#17A2B8] ring-2 ring-[#17A2B8] animate-pulse" : "border-border"}`}>
                <span className="flex items-center gap-2 truncate"><Car className="w-4 h-4 brand-teal shrink-0" /> {vehLabel}</span>
                <ChevronDown className="w-4 h-4 text-muted-foreground shrink-0" />
              </button>
              {vehOpen && (
                <div className="absolute z-[600] mt-1 w-full max-h-64 overflow-y-auto rounded-md border border-border bg-popover shadow-xl p-1" data-testid="history-vehicle-list">
                  <button onClick={() => { setSelVeh([]); }} data-testid="history-veh-all"
                    className="w-full flex items-center gap-2 px-2 py-1.5 rounded text-sm hover:bg-black/10 transition-colors">
                    <span className={`w-4 h-4 rounded border flex items-center justify-center ${selVeh.length === 0 ? "bg-[#17A2B8] border-[#17A2B8]" : "border-border"}`}>{selVeh.length === 0 && <Check className="w-3 h-3 text-white" />}</span>
                    Toute la flotte
                  </button>
                  {vehicles.map((v) => { const VIcon = getVehicleIcon(v.icon); return (
                    <button key={v.id} onClick={() => toggleVeh(v.id)} data-testid={`history-veh-${v.id}`}
                      className="w-full flex items-center gap-2 px-2 py-1.5 rounded text-sm hover:bg-black/10 transition-colors">
                      <span className={`w-4 h-4 rounded border flex items-center justify-center ${selVeh.includes(v.id) ? "bg-[#17A2B8] border-[#17A2B8]" : "border-border"}`}>{selVeh.includes(v.id) && <Check className="w-3 h-3 text-white" />}</span>
                      <VIcon className="w-4 h-4 shrink-0 text-muted-foreground" aria-hidden="true" />
                      <span className="truncate">{v.name} <span className="text-muted-foreground">· {v.plate}</span></span>
                    </button>
                  ); })}
                </div>
              )}
            </div>

            {/* Sélection conducteur */}
            <div className="flex items-center gap-2 rounded-md border border-border bg-secondary/40 px-3 py-2">
              <User className="w-4 h-4 brand-teal shrink-0" />
              <select value={selDriver} onChange={(e) => setSelDriver(e.target.value)} data-testid="history-driver-select"
                className="flex-1 bg-transparent text-sm outline-none cursor-pointer">
                <option value="" className="bg-popover">Tous les conducteurs</option>
                {drivers.map((d) => <option key={d.id} value={d.id} className="bg-popover">{d.name}</option>)}
              </select>
            </div>

            {/* Raccourcis période */}
            <div className={`flex flex-wrap gap-1.5 rounded-md ${agent?.highlight === "period" ? "ring-2 ring-[#17A2B8] animate-pulse p-1" : ""}`}>
              {SHORTCUTS.filter((s) => !s.days || !lim || s.days <= lim).map((s) => (
                <button key={s.id} onClick={() => pickShortcut(s.id)} data-testid={`history-shortcut-${s.id}`}
                  className={`px-2.5 py-1 rounded-full text-[11px] font-semibold border transition-colors ${shortcut === s.id ? "bg-[#17A2B8] text-white border-[#17A2B8]" : "border-border text-muted-foreground hover:border-[#17A2B8] hover:text-foreground"}`}>
                  {s.label}
                </button>
              ))}
            </div>

            <p className="text-[11px] text-muted-foreground capitalize flex items-center gap-1"><CalendarRange className="w-3.5 h-3.5" /> {rangeText}</p>

            <button type="button" onClick={() => setCalOpen((o) => !o)} data-testid="history-calendar-toggle"
              className="w-full flex items-center justify-between rounded-md border border-border bg-secondary/30 px-3 py-1.5 text-[11px] font-semibold text-muted-foreground hover:text-foreground hover:border-[#17A2B8] transition-colors">
              <span className="flex items-center gap-1.5"><CalendarRange className="w-3.5 h-3.5" /> Dates personnalisées</span>
              <ChevronDown className={`w-4 h-4 transition-transform ${calOpen ? "rotate-180" : ""}`} />
            </button>
            {calOpen && (
              <>
                <div className="rounded-md border border-border bg-secondary/30 p-1 flex justify-center" data-testid="history-calendar">
                  <Calendar mode="range" locale={fr} selected={range}
                    onSelect={(r) => { if (r && r.from) { setRange(r); setShortcut("custom"); } }}
                    disabled={[{ after: accountToday() }, ...(range?.from && !range?.to ? [clampFrom(user, range.from)] : [])]} className="scale-95 origin-top" />
                </div>
                {lim && <p className="text-[10px] text-muted-foreground px-1 pt-1" data-testid="history-limit-note">{limitText(user)}</p>}
              </>
            )}

            <button onClick={validate} disabled={loading} data-testid="history-validate-btn"
              className="w-full flex items-center justify-center gap-2 rounded-md bg-[#F5821F] hover:bg-[#F5821F]/90 text-white font-bold text-sm py-2 transition-colors disabled:opacity-60">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} Valider la période
            </button>
          </div>

          {/* Résumé + export + liste */}
          <div className="flex items-center justify-between px-3 py-2 border-b border-border">
            <span className="label-mono flex items-center gap-1"><Route className="w-3.5 h-3.5 brand-teal" /> Trajets</span>
            <span className="data text-[11px] text-muted-foreground" data-testid="history-summary">{data ? `${data.count} · ${data.total_km} km` : "—"}</span>
          </div>

          {trips.length > 0 && (
            <div className="flex items-center gap-1.5 px-3 py-2 border-b border-border" data-testid="history-export-bar">
              <span className="text-[10px] text-muted-foreground uppercase tracking-wider mr-1">Exporter</span>
              <button onClick={() => exportTripsCSV(trips, addr, rangeText)} data-testid="history-export-csv"
                className="flex items-center gap-1 rounded-md border border-border px-2 py-1 text-[11px] font-semibold hover:border-[#17A2B8] hover:text-[#2ec5db] transition-colors"><FileSpreadsheet className="w-3.5 h-3.5" /> CSV</button>
              <button onClick={() => exportTripsPDF(trips, addr, rangeText)} data-testid="history-export-pdf"
                className="flex items-center gap-1 rounded-md border border-border px-2 py-1 text-[11px] font-semibold hover:border-[#17A2B8] hover:text-[#2ec5db] transition-colors"><FileText className="w-3.5 h-3.5" /> PDF</button>
              <button onClick={() => exportTripsGPX(trips, rangeText)} data-testid="history-export-gpx"
                className="flex items-center gap-1 rounded-md border border-border px-2 py-1 text-[11px] font-semibold hover:border-[#17A2B8] hover:text-[#2ec5db] transition-colors"><Download className="w-3.5 h-3.5" /> GPX</button>
              <button onClick={() => exportDriverReportPDF(trips, rangeText)} data-testid="history-export-driver"
                className="flex items-center gap-1 rounded-md border border-border px-2 py-1 text-[11px] font-semibold hover:border-[#17A2B8] hover:text-[#2ec5db] transition-colors"><User className="w-3.5 h-3.5" /> Conducteurs</button>
            </div>
          )}

          {summary && (
            <div className="px-3 py-2 border-b border-border grid grid-cols-2 gap-x-3 gap-y-2 text-[11px]" data-testid="history-period-summary">
              <div><p className="label-mono">Distance totale</p><p className="data font-bold brand-orange">{distStr(summary.totalKm)}</p></div>
              <div><p className="label-mono">Temps de conduite</p><p className="data font-bold">{fmtDur(summary.totalMin)}</p></div>
              <div><p className="label-mono">Véhicule le plus actif</p><p className="font-semibold truncate">{summary.topVeh ? `${summary.topVeh[0]} · ${Math.round(summary.topVeh[1])} km` : "—"}</p></div>
              {summary.topDrv && <div><p className="label-mono">Conducteur le plus actif</p><p className="font-semibold truncate">{`${summary.topDrv[0]} · ${Math.round(summary.topDrv[1])} km`}</p></div>}
            </div>
          )}

          {trips.length > 0 && fuelPriceBasis === "current" && (
            <p className="px-3 py-1.5 border-b border-border text-[10px] text-muted-foreground flex items-start gap-1" data-testid="history-fuel-price-note">
              <Fuel className="w-3 h-3 mt-0.5 shrink-0 brand-orange" />
              <span>Coût carburant = litres CAN × <strong>prix actuel</strong> configuré, appliqué aussi aux trajets passés (l'historique des prix n'est pas conservé).</span>
            </p>
          )}

          <div ref={tripListRef} className="flex-1 overflow-y-auto p-2 pb-20 lg:pb-2 space-y-1.5" data-testid="history-trip-list">
            {!loading && data && trips.length > 0 && (
              <div className="flex items-center gap-2 pb-1">
                <button type="button" onClick={toggleCompareMode} data-testid="compare-toggle"
                  className={`flex-1 inline-flex items-center justify-center gap-1.5 rounded-md px-2.5 py-1.5 text-xs font-bold border transition-colors ${compareMode ? "bg-[#17A2B8] text-white border-[#17A2B8]" : "border-border text-muted-foreground hover:text-foreground hover:border-[#17A2B8]/60"}`}>
                  <GitCompare className="w-3.5 h-3.5" /> Comparer les trajets
                </button>
              </div>
            )}
            {compareMode && (
              <div className="flex items-center gap-2 rounded-md border border-[#17A2B8]/40 bg-[#17A2B8]/10 px-2.5 py-1.5" data-testid="compare-bar">
                <span className="text-xs font-bold" data-testid="compare-count">{compareIdxs.length} / {MAX_COMPARE} trajets sélectionnés</span>
                <button type="button" disabled={compareIdxs.length < 2} onClick={() => setShowComparePanel(true)} data-testid="compare-open"
                  className={`ml-auto text-xs font-bold rounded px-2 py-1 ${compareIdxs.length < 2 ? "opacity-40 cursor-not-allowed border border-border" : "bg-[#17A2B8] text-white"}`}>Comparer</button>
                <button type="button" onClick={() => { setCompareIdxs([]); setShowComparePanel(false); }} data-testid="compare-clear"
                  className="text-xs font-semibold text-muted-foreground hover:text-[#EF4444]">Effacer</button>
              </div>
            )}
            {loading && <div className="py-8 flex justify-center"><BrandLoader size={40} rings={false} label="Chargement des trajets…" /></div>}
            {!loading && data && trips.length === 0 && <p className="text-xs text-muted-foreground py-8 text-center px-3">Aucun trajet sur la période sélectionnée.</p>}
            {!loading && !data && <p className="text-xs text-muted-foreground py-8 text-center px-3">Sélectionnez une période puis « Valider » pour afficher les trajets de la flotte.</p>}
            {trips.map((t, i) => {
              const inCmp = compareIdxs.includes(i);
              const cmpFull = compareIdxs.length >= MAX_COMPARE && !inCmp;
              return (
              <button key={i} type="button" data-testid={`history-trip-${i}`}
                onClick={() => (compareMode ? toggleCompareTrip(i) : selectTrip(i))}
                onMouseEnter={() => { setHoveredIdx(i); if (compareMode && inCmp) setHoverCompareIdx(i); }}
                onMouseLeave={() => { setHoveredIdx(null); setHoverCompareIdx(null); }}
                className={`w-full text-left rounded-md p-2.5 border transition-all ${compareMode ? (inCmp ? "border-[#17A2B8] bg-[#17A2B8]/10" : (cmpFull ? "border-border opacity-50" : "border-border bg-secondary/40 hover:border-[#17A2B8]/60")) : (selectedIdx === i ? "border-[#17A2B8] bg-[#17A2B8]/10" : "border-border bg-secondary/40 hover:border-[#17A2B8]/60")}`}>
                <div className="flex items-center justify-between mb-1.5">
                  <span className="flex items-center gap-1.5 font-bold text-xs truncate">
                    {compareMode && (
                      <span data-testid={`compare-check-${i}`} className={`w-4 h-4 rounded border flex items-center justify-center shrink-0 ${inCmp ? "bg-[#17A2B8] border-[#17A2B8]" : "border-muted-foreground/50"}`}>
                        {inCmp && <Check className="w-3 h-3 text-white" />}
                      </span>
                    )}
                    <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: listColor(i) }} />
                    <span className="truncate">{t.vehicle_name}</span>
                    <span className="text-muted-foreground font-normal shrink-0">· {t.plate}</span>
                  </span>
                  <span className="data text-[11px] text-muted-foreground shrink-0">{fmtDay(t.start)}</span>
                </div>
                <div className="space-y-1 data text-[11px]">
                  <div className="flex items-center gap-1.5">
                    <Flag className="w-3.5 h-3.5 text-[#10B981] shrink-0" />
                    <span className="font-semibold shrink-0">{fmtHM(t.start)}</span>
                    <span className="text-muted-foreground truncate">· {addr[i]?.start || <span className="inline-flex items-center gap-1"><MapPin className="w-3 h-3" />localisation…</span>}</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <Flag className="w-3.5 h-3.5 text-[#EF4444] shrink-0" />
                    <span className="font-semibold shrink-0">{fmtHM(t.end)}</span>
                    <span className="text-muted-foreground truncate">· {addr[i]?.end || "localisation…"}</span>
                  </div>
                </div>
                <div className="flex items-center gap-3 mt-1.5 pt-1.5 border-t border-border/60 text-[10px] text-muted-foreground data">
                  <span className="flex items-center gap-1 brand-orange font-bold"><Route className="w-3 h-3" />{distStr(t.distance_km)}</span>
                  <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{fmtDur(t.duration_min)}</span>
                  <span className="flex items-center gap-1"><Gauge className="w-3 h-3" />{t.max_speed} km/h</span>
                  {fuelMap && (() => {
                    const cell = fuelMap[fuelKey(t.vehicle_id, t.start)];
                    if (!cell) return null;
                    if (cell.cost_xpf != null) {
                      return <span className="flex items-center gap-1 brand-orange font-bold" data-testid={`history-trip-fuel-${i}`} title={fuelCostHint(cell)}><Fuel className="w-3 h-3" />{fmtXPF(cell.cost_xpf)}</span>;
                    }
                    const rr = fuelReason(cell.unavailable_reason);
                    return <span className="flex items-center gap-1" data-testid={`history-trip-fuel-${i}`} title={rr.hint}><Fuel className="w-3 h-3" />{rr.label}</span>;
                  })()}
                </div>
                {t.driver_name && (
                  <div className="flex items-center gap-1 mt-1 text-[10px] font-semibold text-[#17A2B8]" data-testid={`history-trip-driver-${i}`}>
                    <User className="w-3 h-3" /> {t.driver_name}
                  </div>
                )}
                <span role="button" tabIndex={0} data-testid={`history-trip-charts-${i}`}
                  onClick={(e) => { e.stopPropagation(); setChartTrip(t); }}
                  onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); e.stopPropagation(); setChartTrip(t); } }}
                  className="mt-2 inline-flex items-center gap-1.5 text-[11px] font-bold text-[#2ec5db] border border-[#17A2B8]/40 hover:bg-[#17A2B8]/15 rounded-full px-2.5 py-1 cursor-pointer transition-colors">
                  <Cpu className="w-3.5 h-3.5" /> Infos CANbus
                </span>
              </button>
              );
            })}
          </div>
        </div>

        {/* Bouton mobile : voir la carte (depuis la liste) */}
        {mobileView === "list" && data && (
          <button onClick={() => setMobileView("map")} data-testid="history-mobile-show-map"
            className="lg:hidden absolute bottom-4 right-4 z-[600] flex items-center gap-2 rounded-full bg-[#17A2B8] text-white font-bold text-sm px-4 py-2.5 shadow-lg hover:bg-[#17A2B8]/90 transition-colors">
            <MapPin className="w-4 h-4" /> Voir la carte
          </button>
        )}

        {/* Carte */}
        <div className={`${mobileView === "map" ? "block" : "hidden"} lg:block flex-1 relative`} data-testid="history-map-pane">
          {deepLink && deepLink.status !== "ready" && (
            <div className="absolute top-3 left-1/2 -translate-x-1/2 z-[700] glass rounded-md px-3 py-2 text-xs font-semibold max-w-[90%] text-center" data-testid="deeplink-banner">
              {deepLink.status === "loading" && <span className="flex items-center gap-2"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Chargement du contexte de l'épisode…</span>}
              {deepLink.status === "error" && <span className="text-[#EF4444]" data-testid="deeplink-error">Épisode indisponible ou accès refusé.</span>}
              {deepLink.status === "trigger_unavailable" && <span className="text-[#F5821F]" data-testid="deeplink-trigger-unavailable">Position historique indisponible au moment du déclenchement.</span>}
              {deepLink.status === "trip_unavailable" && <span className="text-[#F5821F]" data-testid="deeplink-trip-unavailable">Aucun trajet historique disponible autour du déclenchement.</span>}
            </div>
          )}
          <HistoryMap trips={trips} selectedIdx={selectedIdx} hoveredIdx={hoveredIdx} visible={mobileView === "map"} focusPoint={focusPoint}
            onSelect={(i) => (compareMode ? recenterCompare(i) : setSelectedIdx((s) => (s === i ? null : i)))}
            compareIdxs={compareMode ? compareIdxs : []} hoverCompareIdx={hoverCompareIdx} recenterIdx={recenterIdx} />
          {compareMode && showComparePanel && compareIdxs.length >= 2 && (
            <ComparePanel trips={trips} idxs={compareIdxs} onClose={() => setShowComparePanel(false)}
              onHover={setHoverCompareIdx} onRecenter={recenterCompare} onRemove={removeCompare} />
          )}
          {/* Retour à la liste (mobile) — conserve véhicule, période, filtres, sélection et défilement */}
          <button onClick={backToList} data-testid="history-mobile-back-list"
            className="lg:hidden absolute top-3 left-3 z-[500] glass rounded-md px-3 py-1.5 text-xs font-semibold hover:text-[#17A2B8] transition-colors">
            ← Trajets
          </button>
          {selectedIdx !== null && trips[selectedIdx] && (
            <button onClick={() => setSelectedIdx(null)} data-testid="history-show-all"
              className="absolute top-14 lg:top-3 left-3 lg:left-3 z-[500] glass rounded-md px-3 py-1.5 text-xs font-semibold hover:text-[#17A2B8] transition-colors">
              ← Afficher tous les trajets
            </button>
          )}
        </div>
      </div>
      {chartTrip && <TripChartsModal trip={chartTrip} title={`${chartTrip.vehicle_name || "Trajet"} · ${fmtDay(chartTrip.start)} ${fmtHM(chartTrip.start)}`} onClose={() => setChartTrip(null)} />}
    </Layout>
  );
}

